<template>
  <div>
    <div class="flex items-center mb-5">
      <h3 class="uppercase text-lg font-semibold text-primary tracking-wide">
        Equipment
      </h3>
      <hr class="flex-1 border-primary mx-4" />

      <button
          @click="addEquipment"
          class="flex items-center bg-green-200 border hover:bg-green-300 border-green-400 hover:border-green-500 hover:shadow active:shadow-inner transition duration-300 text-secondary rounded-md px-2 py-1"
      >
        <svg
            class="stroke-current h-5 w-5 mr-2"
            fill="none"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            viewBox="0 0 24 24"
        >
          <path d="M12 5v14M5 12h14" />
        </svg>
        <span>Add Equipment</span>
      </button>
    </div>

    <div class="flex flex-wrap">
      <vue-good-table
          v-if="equipment.length > 0"
          class="w-full"
          styleClass="vgt-table condensed"
          mode="remote"
          :rows="equipment"
          :columns="columns"
          :totalRows="totalEquipmentItems"
          @on-row-click="openEquipmentDetails"
          @on-page-change="onPageChange"
          @on-sort-change="onSortChange"
          @on-column-filter="onColumnFilter"
          @on-per-page-change="onPerPageChange"
          :pagination-options="{
          enabled: true,
          perPage: 50,
          perPageDropdown: [1, 2, 3, 10, 20, 50, 100],
          dropdownAllowAll: false
        }"
      >
        <template slot="table-row" slot-scope="props">
          <span v-if="props.column.field == 'is_loan_unit'" class="block text-center">
            <svg v-if="props.row.is_loan_unit" class="h-4 w-4 mx-auto text-green-600" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><title>Yes</title><path d="M5 13l4 4L19 7" stroke-linecap="round" stroke-linejoin="round"/></svg>
            <svg v-else class="h-4 w-4 mx-auto text-red-400" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><title>No</title><path d="M18 6L6 18M6 6l12 12" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </span>

          <span v-else-if="props.column.field == 'is_operational'" class="block text-center">
            <svg v-if="props.row.is_operational" class="h-4 w-4 mx-auto text-green-600" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><title>Yes</title><path d="M5 13l4 4L19 7" stroke-linecap="round" stroke-linejoin="round"/></svg>
            <svg v-else class="h-4 w-4 mx-auto text-red-400" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><title>No</title><path d="M18 6L6 18M6 6l12 12" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </span>

          <span v-else-if="props.column.field == 'description'">
            <div class="truncate" style="max-width: 260px" :title="props.row.description || ''">
              {{ props.row.description || "-" }}
            </div>
          </span>

          <span v-else-if="props.column.field == 'product_code'">
            <span
                class="text-primary underline cursor-pointer"
                @click.stop="openProductEdit(props.row)"
            >
              {{ props.formattedRow[props.column.field] }}
            </span>
          </span>

          <span v-else-if="props.column.field == 'serial_number'">
            <span
                class="text-primary underline cursor-pointer"
                @click.stop="openEquipmentDetails({ row: props.row })"
            >
              {{ props.formattedRow[props.column.field] }}
            </span>
          </span>

          <span v-else>{{ props.formattedRow[props.column.field] }}</span>
        </template>
      </vue-good-table>

      <p
          class="mx-auto my-16"
          v-if="viewState == 'Idle' && (!equipment || equipment.length == 0)"
      >
        There is no Equipment on this Contact.
      </p>

      <div
          v-if="viewState == 'Loading'"
          class="flex w-full justify-center py-20"
      >
        <Spinner :color="'text-black'" :size="10" />
      </div>
    </div>

    <portal to="overlay-outlet">
      <panel
          :showing="addEditEquipmentPanelOpen"
          @close="closeAddEditEquipment"
          :title="selectedEquipmentId ? 'Edit Equipment' : 'Add Equipment'"
      >
        <AddEquipment
            :customer="contact"
            @refreshEquipment="onEquipmentSaved"
        />
      </panel>
    </portal>
  </div>
</template>

<script>
const Notification = () => import("@/components/Notification.vue");
const ConfirmAlert = () => import("@/components/ConfirmAlert.vue");
const Panel = () => import("@/components/Panel.vue");
const Spinner = () => import("@/components/Spinner.vue");
const AddEquipment = () => import("@/components/Equipment/AddEquipment.vue");

function formatUnixToUKDate(unixSeconds) {
  if (!unixSeconds || typeof unixSeconds !== "number") return "-";
  const d = new Date(unixSeconds * 1000);
  if (isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-GB");
}

function enrichEquipmentWithExpiry(equipment) {
  const calibrations = equipment && equipment.calibrations ? equipment.calibrations : [];
  let latestCalibration = null;
  let latestService = null;

  calibrations.forEach(function (cal) {
    if (cal && cal.job_type === "CALIBRATION") {
      if (!latestCalibration || cal.cert_expiry_date > latestCalibration) {
        latestCalibration = cal.cert_expiry_date;
      }
    }

    if (cal && cal.job_type === "SERVICE") {
      if (!latestService || cal.cert_expiry_date > latestService) {
        latestService = cal.cert_expiry_date;
      }
    }
  });

  let latestWarranty = equipment && equipment.warranty_expiry ? equipment.warranty_expiry : null;

  if (equipment && equipment.contracts && equipment.contracts.length > 0) {
    equipment.contracts.forEach(function (c) {
      if (c && c.end_date && (!latestWarranty || c.end_date > latestWarranty)) {
        latestWarranty = c.end_date;
      }
    });
  }

  return Object.assign({}, equipment, {
    latestCalibrationExpiry: latestCalibration,
    latestServiceExpiry: latestService,
    latestWarrantyExpiry: latestWarranty
  });
}

export default {
  name: "Equipment",
  components: {
    Notification,
    ConfirmAlert,
    Panel,
    Spinner,
    AddEquipment
  },
  props: {
    contactId: String,
    contact: Object
  },
  data() {
    return {
      equipment: [],
      viewState: "Idle",
      totalEquipmentItems: 0,
      addEditEquipmentPanelOpen: false,
      selectedEquipmentId: "",
      selectedEquipment: null,
      pagingParams: {
        page: 1,
        limit: 50
      },
      confirmAlert: {
        resultFunction: "",
        showing: false,
        message: "",
        title: "",
        data: ""
      },
      columns: [
        {
          label: "Product Code",
          field: "product_code"
        },
        {
          label: "Serial No.",
          field: "serial_number"
        },
        {
          label: "Make",
          field: "make"
        },
        {
          label: "Model",
          field: "model_name"
        },
        {
          label: "Description",
          field: "description",
          sortable: false
        },
        {
          label: "Category",
          field: "category"
        },
        {
          label: "Service Expiry",
          field: "service_expiry"
        },
        {
          label: "Calibration Expiry",
          field: "calibration_expiry"
        },
        {
          label: "Location",
          field: "location"
        },
        {
          label: "Loan Unit",
          field: "is_loan_unit",
          width: "115px",
          thClass: "whitespace-nowrap"
        },
        {
          label: "Operational",
          field: "is_operational",
          width: "115px",
          thClass: "whitespace-nowrap"
        }
      ]
    };
  },
  async mounted() {
    this.viewState = "Loading";
    await this.loadEquipment();
    this.viewState = "Idle";
  },
  methods: {
    addEquipment() {
      this.selectedEquipmentId = "";
      this.selectedEquipment = null;
      this.addEditEquipmentPanelOpen = true;
    },

    editEquipment(row) {
      if (!row) return;
      this.selectedEquipmentId = row.equipment_id;
      this.selectedEquipment = Object.assign({}, row);
      this.addEditEquipmentPanelOpen = true;
    },

    closeAddEditEquipment() {
      this.addEditEquipmentPanelOpen = false;
    },

    async onEquipmentSaved() {
      this.closeAddEditEquipment();
      await this.loadEquipment();
    },

    openProductEdit(row) {
      if (!row) return;
      if (!row.product_code) return;

      this.$router.push({
        name: "product-edit",
        params: { productCode: row.product_code }
      });
    },

    async loadEquipment() {
      try {
        let result = await this.ContactService.getEquipment(
            this.contactId,
            this.pagingParams
        );

        const data = result && result.data ? result.data : [];
        const rows = data.map(function (e) {
          const enriched = enrichEquipmentWithExpiry(e);

          return Object.assign({}, enriched, {
            // The product's live description, with the equipment's own text as a fallback.
            description: enriched && (enriched.product_description || enriched.description || enriched.name)
                ? enriched.product_description || enriched.description || enriched.name
                : "-",
            service_expiry: formatUnixToUKDate(enriched.latestServiceExpiry),
            calibration_expiry: formatUnixToUKDate(enriched.latestCalibrationExpiry)
          });
        });

        this.equipment = rows;
        this.totalEquipmentItems = result && result.count ? result.count : 0;
      } catch (error) {
        console.error(error);
        this.viewState = "Error";
      }
    },

    async onPageChange(params) {
      this.updatePagingParams({ page: params.currentPage });
      await this.loadEquipment();
    },

    async onPerPageChange(params) {
      this.updatePagingParams({
        limit: params.currentPerPage,
        page: params.currentPage
      });
      await this.loadEquipment();
    },

    async onSortChange(params) {
      this.updatePagingParams({
        order: params[0].type,
        orderBy: params[0].field
      });
      await this.loadEquipment();
    },

    updatePagingParams(newProps) {
      this.pagingParams = Object.assign({}, this.pagingParams, newProps);
    },

    onColumnFilter(params) {
      console.log("onColumnFilter", params);
    },

    openEquipmentDetails(selectedRow) {
      if (!selectedRow) return;
      if (!selectedRow.row) return;
      if (!selectedRow.row.equipment_id) return;

      this.$router.push({
        name: "equipment-detail",
        params: { equipmentId: selectedRow.row.equipment_id }
      });
    }
  }
};
</script>